import { Localized, useLocalization } from "@fluent/react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    Switch,
    Typography,
    Box,
    Skeleton,
    Stack,
    Avatar,
    Autocomplete,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { AppId, AppTextFieldHandler } from "../../../../app/appTypes";
import { SaveButton } from "../../../../app/components/SaveButton";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { RequiredError } from "../../../../app/components/RequiredError";
import { CancelButton } from "../../../../app/components/CancelButton";
import { DocumentLanguages, DocumentTypes } from "../../../documents/documentTypes";
import { DocumentLanguageSelect } from "../../../documents/controls/DocumentLanguageSelect";
import { alpha } from "@mui/material/styles";
import {
    addStageDocument,
    AddStageDocumentPayload,
    CreationMode,
    DocumentToCreate,
    ProcessStageOverview,
} from "../validationProcessApi";
import { ClientDocumentTemplateSelectField } from "../../../documents/templates/ClientDocumentTemplateSelectField";
import { LoadUsers } from "../../../../app/AppDataLoader";
import PersonIcon from "@mui/icons-material/Person";
import { selectAllUsers, User } from "../../../users/usersSlice";
import React from "react";
import { buildDocumentNumberByProcess, buildDocumentTitleByProcess } from "../utils/documentNumberingHelper";
import { selectDeviceById } from "../../../device/deviceSlice";
import DocumentTargetInfo from "./DocumentTargetInfo";
import { DocumentNumberTextField } from "../../../documents/controls/DocumentNumberTextField";
import { prepareValidationStage, PrepareValidationStagePayload } from "../validationProcessApi";

type RiskAnalysisSourceMode = "EMPTY" | "FROM_URS" | "FROM_ACF";

export interface AddStageDocumentProps {
    processId: AppId | null;
    stageId: string;
    documentType: DocumentTypes;
    structureId: number;
    processName?: string;
    processNumber?: string;
    deviceId?: string;
    open?: boolean;
    titleL10nId?: string;
    defaultTitle?: string;

    onSuccess: (stage: ProcessStageOverview) => void;
    onCancel: () => void;
}

export const AddStageDocument: React.FC<AddStageDocumentProps> = (props) => {
    const {
        processId,
        stageId,
        documentType,
        structureId,
        processName,
        processNumber,
        deviceId,
        open = true,
        titleL10nId,
        defaultTitle,
        onSuccess,
        onCancel,
    } = props;

    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();

    const allUsers = useAppSelector(selectAllUsers);
    const device = useAppSelector((state) =>
        deviceId ? selectDeviceById(state, deviceId) : null
    );

    const [language, setLanguage] = useState<DocumentLanguages | "">("");
    const [documentNumber, setDocumentNumber] = useState("");
    const [docNoHasError, setDocNoHasError] = useState(false);
    const [title, setTitle] = useState("");
    const [isIshikawa, setIsIshikawa] = useState(false);
    const [riskAnalysisSourceMode, setRiskAnalysisSourceMode] = useState<RiskAnalysisSourceMode>("EMPTY");
    const [responsible, setResponsible] = useState<User | null>(null);
    const [responsibleDirty, setResponsibleDirty] = useState(false);

    const [creationMode, setCreationMode] = useState<CreationMode>("TEMPLATE");
    const [templateId, setTemplateId] = useState<string | "">("");

    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const [numberDirty, setNumberDirty] = useState(false);
    const [titleDirty, setTitleDirty] = useState(false);

    const isRiskAnalysisDoc = documentType === "RA";

    const isResponsibleOk = !!(responsible && responsible.userName !== "");

    const isBasicsOk =
        !!documentNumber.trim() &&
        !docNoHasError &&
        !!title.trim() &&
        !!language &&
        !!processId &&
        isResponsibleOk;

    const isTemplateOk =
        creationMode === "EMPTY"
            ? true
            : templateId !== "" && Number(templateId) > 0;

    const isFormValid = isBasicsOk && isTemplateOk;

    useEffect(() => {
        if (!numberDirty) {
            setDocumentNumber(
                buildDocumentNumberByProcess(processNumber, String(documentType))
            );
        }
        if (!titleDirty) {
            if (language) {
                setTitle(
                    buildDocumentTitleByProcess(
                        language,
                        String(documentType),
                        device ?? undefined
                    )
                );
            } else {
                setTitle("");
            }
        }
    }, [
        documentType,
        language,
        device?.name,
        device?.serialNo,
        processNumber,
        numberDirty,
        titleDirty,
    ]);

    // --- handlery
    const handleTitleChange: AppTextFieldHandler = useCallback((e) => {
        setTitle(e.target.value);
        setTitleDirty(true);
    }, []);

    const handleDocumentNumberChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setDocumentNumber(e.target.value);
        setNumberDirty(true);
    };

    const handleModeChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setCreationMode(e.target.value as CreationMode);
        },
        []
    );

    const handleRiskAnalysisSourceModeChange = useCallback((_: React.MouseEvent<HTMLElement>, value: RiskAnalysisSourceMode | null) => {
        if (!value) return;

        setRiskAnalysisSourceMode(value);
    }, []);

    const handleLanguageChange = useCallback((lang: DocumentLanguages | "") => {
        if (lang) setLanguage(lang);
    }, []);

    const handleSaveClick = async () => {
        if (!isFormValid) {
            setServerError("fill-all-required-fields");
            setResponsibleDirty(true);
            return;
        }

        const normalizedTemplateId =
            creationMode === "TEMPLATE" ? Number(templateId) : 0;

        const doc: DocumentToCreate =
        {
            type: documentType,
            number: documentNumber.trim(),
            title: title.trim(),
            language,
            responsible: responsible?.userName || null,
            creationMode: creationMode,
            templateId: normalizedTemplateId,
            ...(isRiskAnalysisDoc
                ? {
                    raSettings: {
                        isIshikawa,
                        LoadFromACF: riskAnalysisSourceMode === "FROM_ACF",
                        loadRequirementsFromUrs: riskAnalysisSourceMode === "FROM_URS",
                    },
                }
                : {}),
        };


        setSaving(true);
        setServerError("");
        try {
            if (isRiskAnalysisDoc) {
                const payload: PrepareValidationStagePayload = {
                    stageCode: String(documentType),
                    processId: processId!,
                    documents: [doc],
                    toLink: [],
                };

                const result = await dispatch(prepareValidationStage(payload));
                if (prepareValidationStage.fulfilled.match(result)) {
                    const stage = result.payload as ProcessStageOverview;
                    dispatch(showSuccess("saved"));
                    onSuccess(stage);
                } else {
                    let errorMsg = "error";
                    if (result.payload && result.payload.kind === "http") {
                        const problem = result.payload.problem;
                        if (problem) {
                            errorMsg = problem.title;
                        }
                    }
                    setServerError(errorMsg);
                }
                return;
            }

            const payload: AddStageDocumentPayload = {
                stageId: stageId,
                processId: processId!,
                document: doc,
            };

            const result = await dispatch(addStageDocument(payload));
            if (addStageDocument.fulfilled.match(result)) {
                const stage = result.payload as ProcessStageOverview;
                dispatch(showSuccess("saved"));
                onSuccess(stage);
            } else {
                let errorMsg = "error";
                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem;
                    if (problem) {
                        errorMsg = problem.title;
                    }
                }
                setServerError(errorMsg);
            }
        } catch (e) {
            setServerError("error");
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    const dialogTitleContent = (
        <span>
            {defaultTitle ??
                `Przygotowanie dokumentu ${String(documentType).toUpperCase()}`}
        </span>
    );

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            slotProps={{
                backdrop: {
                    sx: {
                        bgcolor: (t) => alpha(t.palette.grey[900], 0.5),
                    },
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                {titleL10nId ? (
                    <Localized id={titleL10nId}>{dialogTitleContent}</Localized>
                ) : (
                    dialogTitleContent
                )}
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                <DocumentTargetInfo
                    objectId={deviceId}
                    objectType="DEVICE"
                    processNumber={processNumber}
                    processTitle={processName}
                    structureId={structureId}
                />

                {/* 1) Numer + Język */}
                <Grid container spacing={2} sx={{ mt: 1, mb: 0.5 }}>
                    <Grid item xs={12} md={8}>
                        <DocumentNumberTextField
                            fullWidth={true}
                            required={true}
                            value={documentNumber}
                            onChange={handleDocumentNumberChange}
                            label={
                                <Localized id="document-number">
                                    <span>_Numer dokumentu</span>
                                </Localized>
                            }
                            documentType={documentType}
                            onErrorChange={(err) => setDocNoHasError(err)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <DocumentLanguageSelect
                            defaultValue={language}
                            required
                            onChange={handleLanguageChange}
                        />
                    </Grid>
                </Grid>

                {/* 2) Tytuł */}
                <Grid container spacing={2} sx={{ mt: 1, mb: 0.5 }}>
                    <Grid item xs={12}>
                        <TextField
                            helperText={title === "" && <RequiredError />}
                            required
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={title}
                            error={title === ""}
                            onChange={handleTitleChange}
                            label={
                                <Localized id="document-title">
                                    <span>_Tytuł</span>
                                </Localized>
                            }
                        />
                    </Grid>
                </Grid>

                {/* 3) Osoba odpowiedzialna */}
                <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                    <Grid item xs={12}>
                        <LoadUsers component={<Skeleton variant="rectangular" height={180} />}>
                            <Autocomplete
                                options={allUsers}
                                onChange={(_, u) => {
                                    setResponsible(u ?? null);
                                    setResponsibleDirty(true);
                                }}
                                onBlur={() => setResponsibleDirty(true)}
                                value={responsible}
                                isOptionEqualToValue={(o, v) => o.userName === v.userName}
                                getOptionLabel={(u) =>
                                    u ? `${u.firstname} ${u.surname} (${u.email})` : ""
                                }
                                renderOption={(props, u) => (
                                    <li {...props} key={u.userName}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    fontSize: 12,
                                                    lineHeight: 1,
                                                }}
                                                src={(u as any).avatarUrl}
                                            >
                                                <PersonIcon sx={{ fontSize: 16 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {u.surname} {u.firstname}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {u.email}
                                                    {u.position ? ` · ${u.position}` : ""}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={
                                            <Localized id="document-responsible-person-for-preparing">
                                                <span>_responsible</span>
                                            </Localized>
                                        }
                                        placeholder={l10n.getString(
                                            "choose-person",
                                            null,
                                            "_choose-persone"
                                        )}
                                        required
                                        error={responsibleDirty && !isResponsibleOk}
                                        helperText={
                                            responsibleDirty && !isResponsibleOk ? (
                                                <RequiredError />
                                            ) : null
                                        }
                                    />
                                )}
                            />
                        </LoadUsers>
                    </Grid>
                </Grid>

                {/* 4) Tryb tworzenia */}
                <Box
                    sx={(t) => ({
                        border: `1px solid ${t.palette.divider}`,
                        borderRadius: 1,
                        p: 1.25,
                        mt: 1,
                    })}
                >
                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                        <Localized id="document-creation-mode">
                            <span>_Tryb tworzenia</span>
                        </Localized>
                    </Typography>
                    <RadioGroup row value={creationMode} onChange={handleModeChange}>
                        <FormControlLabel
                            value="EMPTY"
                            control={<Radio size="small" />}
                            sx={{ mr: 2 }}
                            label={
                                <Typography variant="body2">
                                    <Localized id="document-creation-mode-empty">
                                        <span>_Pusty</span>
                                    </Localized>
                                </Typography>
                            }
                        />
                        <FormControlLabel
                            value="TEMPLATE"
                            control={<Radio size="small" />}
                            label={
                                <Typography variant="body2">
                                    <Localized id="document-creation-mode-template">
                                        <span>_template</span>
                                    </Localized>
                                </Typography>
                            }
                        />
                    </RadioGroup>

                    {creationMode === "TEMPLATE" && (
                        <Box sx={{ mt: 1 }}>
                            <ClientDocumentTemplateSelectField
                                structureId={structureId}
                                documentType={documentType}
                                value={templateId}
                                onChange={(id) => setTemplateId(id)}
                            />
                        </Box>
                    )}
                </Box>

                {isRiskAnalysisDoc && (
                    <Box
                        sx={(t) => ({
                            mt: 2,
                            border: `1px solid ${t.palette.divider}`,
                            borderRadius: 1,
                            px: 2,
                            py: 1.5,
                        })}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Ustawienia
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={isIshikawa}
                                        onChange={(e) => setIsIshikawa(e.target.checked)}
                                    />
                                }
                                label="Ishikawa"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={riskAnalysisSourceMode === "FROM_URS"}
                                        onChange={(e) =>
                                            setRiskAnalysisSourceMode(
                                                e.target.checked ? "FROM_URS" : "EMPTY"
                                            )
                                        }
                                    />
                                }
                                label={<Localized id="risk-analysis-load-from-urs">_Załaduj wymagania z URS</Localized>}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={riskAnalysisSourceMode === "FROM_ACF"}
                                        onChange={(e) =>
                                            setRiskAnalysisSourceMode(
                                                e.target.checked ? "FROM_ACF" : "EMPTY"
                                            )
                                        }
                                    />
                                }
                                label={<Localized id="risk-analysis-load-from-acf">_Załaduj krytyczne funkcjonalności z ACF</Localized>}
                            />
                        </Box>
                    </Box>
                )}

                {serverError && (
                    <Alert
                        sx={{
                            mt: 2,
                            position: "sticky",
                            bottom: 0,
                            m: 0,
                            borderRadius: 0,
                        }}
                        onClose={() => setServerError("")}
                        severity="error"
                    >
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <SaveButton loading={saving} onClick={handleSaveClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
